export const BUSINESS_TIMEZONE = 'America/Lima';

export function toBusinessDate(
  date: string | Date | null | undefined,
  position: 'start' | 'end' = 'start',
): Date | null {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  if (isNaN(d.getTime())) return null;
  if (position === 'start') {
    d.setHours(0, 0, 0, 0);
  } else {
    d.setHours(23, 59, 59, 999);
  }
  return d;
}

/**
 * Retorna el instante actual como objeto Date (hora local del negocio)
 */
export function getBusinessNow(): Date {
  return new Date();
}

/**
 * Obtiene la fecha actual en formato ISO (YYYY-MM-DD)
 */
export function getBusinessTodayISODate(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(new Date());
}

/**
 * Asegura que una fecha esté en formato ISO (YYYY-MM-DD) en zona horaria Lima
 */
export function ensureBusinessDateIso(date?: string | Date): string {
  if (!date) return getBusinessTodayISODate();
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return getBusinessTodayISODate();
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(d);
}

/**
 * Desplaza una fecha por un número de días (en zona horaria Lima)
 */
export function shiftBusinessDate(date: string | Date, days: number): string {
  const limaDate = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? date
    : new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(
        typeof date === 'string' ? new Date(date) : date,
      );
  const [y, m, d] = limaDate.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Rango de fechas por defecto para filtros de listado: del 1 de enero al 31 de diciembre del año actual.
 */
export function getDefaultDateRangeEsteAnio(): { fechaDesde: string; fechaHasta: string } {
  return {
    fechaDesde: getStartOfYearISODate(),
    fechaHasta: getEndOfYearISODate(),
  };
}

export function getStartOfMonthISODate(): string {
  const limaDate = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(new Date());
  return limaDate.substring(0, 7) + '-01';
}

export function getEndOfMonthISODate(): string {
  const limaDate = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(new Date());
  const [y, m] = limaDate.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

export function getMonthRangeISODate(year: number, month: number): { fechaDesde: string; fechaHasta: string } {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    fechaDesde: `${year}-${String(month).padStart(2, '0')}-01`,
    fechaHasta: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

export function getStartOfYearISODate(): string {
  const limaDate = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(new Date());
  return limaDate.substring(0, 4) + '-01-01';
}

export function getEndOfYearISODate(): string {
  const limaDate = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(new Date());
  return limaDate.substring(0, 4) + '-12-31';
}

/**
 * Calcula la diferencia en días entre dos fechas
 */
export function daysBetween(startDate: string | Date, endDate: string | Date): number {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Formatea una fecha de forma corta: DD/MM/YYYY
 */
export function formatBusinessDateShort(date: string | Date | null | undefined, locale: string = 'es-PE'): string {
  if (!date) return '';
  let d: Date;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, day] = date.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = typeof date === 'string' ? new Date(date) : date;
  }
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: BUSINESS_TIMEZONE,
  });
}

/**
 * Formatea una fecha para mostrar
 */
export function formatBusinessDate(date: string | Date, locale: string = 'es-PE'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: BUSINESS_TIMEZONE,
  });
}

/**
 * Formatea fecha y hora para mostrar en ticket/comprobante
 */
export function formatBusinessDateTimeForTicket(date: string | Date, locale: string = 'es-PE'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: BUSINESS_TIMEZONE,
  });
}

/**
 * Formatea fecha y hora en formato YYYY-MM-DDTHH:MM (compatible con input datetime-local).
 * Si no se provee fecha, usa la fecha actual.
 */
export function formatBusinessDateTimeLocal(date?: string | Date): string {
  const d = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Parsea un string datetime-local (YYYY-MM-DDTHH:MM) a Date.
 * Retorna null si el string es inválido.
 */
export function parseBusinessDateTimeLocal(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Convierte un Date a string ISO de fecha (YYYY-MM-DD) en zona horaria Lima
 */
export function formatDateToBusinessIso(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(date);
}

/**
 * Extrae las partes de una fecha: year, month, day, hour, minute, second
 */
export function getBusinessDateParts(date: Date): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  };
}

/**
 * Devuelve la fecha/hora en formato ISO 8601 (UTC)
 * Ej: "2025-06-15T14:30:00.000Z"
 */
export function formatBusinessDateTimeIso(date: string | Date): string {
  return new Date(date).toISOString();
}

/**
 * Combina una fecha de negocio (YYYY-MM-DD) con la hora de un objeto Date de referencia.
 * Útil para componer la fecha de emisión con la hora actual del sistema.
 */
export function composeBusinessDateTime(datePart: string | Date, timePart: Date): Date {
  const dateStr = typeof datePart === 'string' ? datePart : datePart.toISOString().split('T')[0];
  const result = new Date(dateStr);
  if (isNaN(result.getTime())) {
    throw new Error(`composeBusinessDateTime: fecha inválida "${String(datePart)}"`);
  }
  result.setHours(timePart.getHours(), timePart.getMinutes(), timePart.getSeconds(), timePart.getMilliseconds());
  return result;
}

export function getBusinessDefaultValidityRange(): { validFrom: string; validUntil: string | null } {
  return {
    validFrom: getBusinessTodayISODate(),
    validUntil: null,
  };
}

/**
 * Convierte un string de fecha en un objeto Date al inicio ('start') o final ('end') del día.
 * Lanza un error si la fecha es inválida (assert).
 */
export function assertBusinessDate(
  date: string | Date,
  position: 'start' | 'end' | 'mid' = 'start',
): Date {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  if (isNaN(d.getTime())) {
    throw new Error(`assertBusinessDate: fecha inválida "${String(date)}"`);
  }
  if (position === 'start') {
    d.setUTCHours(0, 0, 0, 0);
  } else if (position === 'end') {
    d.setUTCHours(23, 59, 59, 999);
  } else {
    d.setUTCHours(12, 0, 0, 0);
  }
  return d;
}
